// AutoTextarea — a <textarea> that grows to fit its content instead of
// scrolling inside a fixed box. Height tracks the value (and re-measures on
// window resize, since wrapping changes with width). Pass a `min-h-*` in
// `className` for the starting/минimum height; user drag-resize is disabled
// because the height is managed.
import { useLayoutEffect, useRef } from "react";

type Props = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export function AutoTextarea({ value, className, style, ...rest }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    // Collapse first so scrollHeight reflects content, not the last height;
    // a `min-height` in className then floors the result.
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  // Re-fit whenever the value changes (typing or an external set, e.g. after
  // Generate-with-AI) and on mount. useLayoutEffect avoids a first-paint flash.
  useLayoutEffect(resize, [value]);

  useLayoutEffect(() => {
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  return (
    <textarea
      ref={ref}
      value={value}
      className={className}
      onInput={resize}
      style={{ resize: "none", overflow: "hidden", ...style }}
      {...rest}
    />
  );
}
